import { IComboBoxNumberOption } from "@coherence-design-system/controls";
import { Pagination } from "@coherence-design-system/pagination";
import { IButtonProps, PivotItem } from "@fluentui/react";
import React from "react";

export enum OverflowOption {
  overflowAndPageDropdown = "overflowAndPageDropdown",
  textfieldAndPageDropdown = "textfieldAndPageDropdown",
}

export enum AutoComplete {
  on = "on",
  off = "off",
}

export interface IPageControls {
  onPivotClick: (
    pivot?: PivotItem | undefined,
    _ev?: React.MouseEvent | undefined
  ) => void;
  pages: number;
  currentPage: number;
  overflowOption: OverflowOption;
  pageDropdownWidth: number;
}

export interface IItemsPerPageProps {
  currentPageSize: number;
  itemsPerPageList: IComboBoxNumberOption[];
  onItemsPerPageChange: (
    _event: React.FormEvent<IComboBoxNumberOption>,
    pageNumber: number
  ) => void;
}

export interface IPaginationProps {
  onPageChange: (
    _event: React.MouseEvent<HTMLButtonElement>,
    selectedPage: number
  ) => void;
  pageControls: IPageControls;
  itemsPerPageProps: IItemsPerPageProps;
  overflowButtonProps: IButtonProps;
  previousPage: IButtonProps;
  nextPage: IButtonProps;
}

export interface IPaginationComponentProps {
  totalPages: number;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  currentPageSize: number;
  setCurrentPageSize: React.Dispatch<React.SetStateAction<number>>;
}

const PaginationComponent: React.FunctionComponent<
  IPaginationComponentProps
> = (props) => {
  const {
    totalPages,
    currentPage,
    setCurrentPage,
    currentPageSize,
    setCurrentPageSize,
  } = props;

  const itemsPerPageList: IComboBoxNumberOption[] = [
    { key: 20, text: "20" },
    { key: 40, text: "40" },
    { key: 60, text: "60" },
    { key: 80, text: "80" },
  ];

  // Pagination setup
  const [currentOverflowOption, setCurrentOverflowOption] = React.useState<
    OverflowOption | undefined
  >();

  const handleOnPageChange = (
    _event: React.MouseEvent<HTMLButtonElement>,
    selectedPage: number
  ): void => {
    setCurrentPage(selectedPage);
  };

  const handleOverflowClick: NonNullable<
    IPaginationProps["overflowButtonProps"]
  >["onClick"] = (_event) => {
    const updateOverflow = currentOverflowOption
      ? undefined
      : OverflowOption.textfieldAndPageDropdown;
    setCurrentOverflowOption(updateOverflow);
  };

  const handlePivotClick = (
    pivot?: PivotItem | undefined,
    _ev?: React.MouseEvent | undefined
  ): void => {
    const currentNumber = parseInt(pivot?.props?.headerText || "1");
    setCurrentPage(currentNumber);
  };

  const handleOnItemsPerPageChange = (
    _event: React.FormEvent<IComboBoxNumberOption>,
    pageNumber: number
  ): void => {
    setCurrentPageSize(pageNumber);
    setCurrentPage(1);
  };

  const paginationProps: IPaginationProps = {
    previousPage: {
      ariaLabel: "back aria label",
    },
    nextPage: {
      ariaLabel: "next aria label",
    },
    overflowButtonProps: {
      onClick: handleOverflowClick,
    },
    pageControls: {
      currentPage: currentPage,
      pages: totalPages,
      overflowOption: currentOverflowOption!,
      onPivotClick: handlePivotClick,
      pageDropdownWidth: 80,
    },
    onPageChange: handleOnPageChange,
    itemsPerPageProps: {
      currentPageSize: currentPageSize,
      itemsPerPageList: itemsPerPageList,
      onItemsPerPageChange: handleOnItemsPerPageChange,
    },
  };

  return <Pagination {...paginationProps} />;
};

export default PaginationComponent;
